import React from 'react';
import { Link } from 'react-router-dom';
import { Typography } from 'antd';
// Helpers
import {
  formatMoney,
  getCardLabel,
  getOrderStatusMeta,
  OrderRow,
} from 'pages/Events/helpers';
// Constants
import { CUSTOMER_PURCHASES_GEN } from 'constants/routes';
// Components
import StatusTag from 'pages/Events/components/StatusTag/StatusTag';
import OrderRefundNote from 'pages/Events/components/OrderRefundNote/OrderRefundNote';
// Styles
import styles from './OrderCells.module.scss';

const { Text } = Typography;

type OrderCellProps = {
  order: OrderRow;
};

export const OrderReference = ({ order }: OrderCellProps): JSX.Element => (
  <Text code copyable={{ text: order.reference }}>
    {order.reference}
  </Text>
);

export const OrderCustomer = ({
  order,
  linkToBuyer = false,
}: OrderCellProps & { linkToBuyer?: boolean }): JSX.Element => (
  <div className={styles.stack}>
    {linkToBuyer && order.buyerUserId ? (
      <Link to={`${CUSTOMER_PURCHASES_GEN}/${order.buyerUserId}`}>
        {order.customerName || '—'}
      </Link>
    ) : (
      <span>{order.customerName || '—'}</span>
    )}
    {order.email && (
      <Text type="secondary" copyable className={styles.small}>
        {order.email}
      </Text>
    )}
  </div>
);

export const OrderTickets = ({ order }: OrderCellProps): JSX.Element => (
  <div className={styles.stack}>
    {order.lines.map((line, index) => (
      <span key={`${line.tierLabel}-${index}`}>
        {line.admissionCount} × {line.tierLabel}
      </span>
    ))}
  </div>
);

export const OrderPayment = ({
  order,
  showPromoCode = false,
}: OrderCellProps & { showPromoCode?: boolean }): JSX.Element => {
  const card = getCardLabel(order);

  return (
    <div className={styles.stack}>
      <span>{formatMoney(order.amount)}</span>
      {card && (
        <Text type="secondary" className={styles.small}>
          {card}
        </Text>
      )}
      {showPromoCode && order.promocodeCode && (
        <Text type="secondary" className={styles.small}>
          {order.promocodeCode} (−{formatMoney(order.discountAmount)})
        </Text>
      )}
    </div>
  );
};

export const OrderStatus = ({
  order,
  timezone,
}: OrderCellProps & { timezone?: string | null }): JSX.Element => (
  <>
    <StatusTag meta={getOrderStatusMeta(order.status, order.kind)} />
    <OrderRefundNote order={order} timezone={timezone} />
  </>
);
